import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep
} from 'lodash';

import { makeStyles } from '@mui/styles';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Accordian from '@mui/material/Accordion';
import AccordianSummary from '@mui/material/AccordionSummary';
import AccordianDetails from '@mui/material/AccordionDetails';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';

import Syslog from '../../../components/system/syslog';
import ESRest from '../../../components/system/es';
import Raw from '../../../components/system/raw';
import Zeek from '../../../components/system/zeek';
import Netflow from '../../../components/system/netflow';
import IPFix from '../../../components/system/ipfix';
import Kafka from '../../../components/system/kafka';
import Webhook from '../../../components/system/webhook';
import Snmp from '../../../components/system/snmp';

import SystemService from '../../../data/system-service';
import defaultExportConfigs from '../../../data/eexportd';
import Auth from '../../../data/auth-service';
import DataContext from '../../../data/data-context';

const useStyles = makeStyles(theme => ({
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    appbarItem: {
        lineHeight: '48px',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        '& > .MuiBox-root': {
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
        }
    },
    tabBar: {
        background: theme.styled.paper.background,
        color: theme.palette.primary.main,
    },
    tabBG: {
        background: theme.palette.background.default,
    },
    tab: {
        paddingLeft: '5px',
        paddingRight: '5px',
        minWidth: '60px',
    },
    marginLeft: {
        marginLeft: theme.spacing(3),
    },
}));

const Event = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const syslogRef = useRef([]);
    const elkRef = useRef([]);
    const rawRef = useRef([]);
    const zeekRef = useRef([]);
    const netflowRef = useRef([]);
    const ipfixRef = useRef([]);
    const kafkaRef = useRef([]);
    const webhookRef = useRef([]);
    const snmpRef = useRef([]);
    const [addExport, setAddExport] = useState(false);
    const [addExportName, setAddExportName] = useState('');
    const { systemSettings, updateSystemSettings } = useContext(DataContext);
    const [exports, setExports] = useState({});
    const [original, setOriginal] = useState({});
    const [changes, setChanges] = useState(false);
    const [eventExportsOpen, setEventExportsOpen] = useState([]);
    const [isFooter, setIsFooter] = useState(false);
    const [systemConstants, setSystemConstants] = useState({
        facilities: [],
        severities: [],
        timezones: [],
    });
    const [template, setTemplate] = useState('Default');
    const [newExports, setNewExports] = useState({});
    const [valid, setValid] = useState({});
    const cmcExportPerms = Auth.permissions.get('CMCExport');

    const defaultConfigs = { ...defaultExportConfigs };

    useEffect(() => {
        if (systemSettings && systemSettings.eexportd) {
            setEventExportsOpen(Object.keys(systemSettings.eexportd).map(() => false));
            setValid(Object.keys(systemSettings.eexportd).reduce((a, c) => {
                a[c] = true;
                return a;
            }, {}));
            setOriginal(_cloneDeep(systemSettings.eexportd));
            setExports(_cloneDeep(systemSettings.eexportd));
        }
        if (systemSettings && systemSettings.ui) {
            setIsFooter(Boolean(systemSettings.ui.messages.footer));
        }
    }, [systemSettings]);

    useEffect(() => {
        setChanges(!_isEqual(exports, original));
    }, [exports, original]);

    useEffect(() => {
        defaultConfigs.Default.event_types.push('anomaly');
        SystemService.getConstants().then((res) => {
            setSystemConstants(res);
        });
    }, []);

    const handleChanges = (name, changes, data) => {
        setValid({ ...valid, [name]: !changes });

        const e = { ...exports };
        e[name] = data;
        setExports(e);
    };

    const handleFacilityLabel = (si) => {
        for (const v in systemConstants.facilities) {
            if (systemConstants.facilities[v] === si) return v;
        }
        return si;
    };

    const handleSeverityLabel = (si) => {
        for (const v in systemConstants.severities) {
            if (systemConstants.severities[v] === si) return v;
        }
        return si;
    };

    const handleAddExport = () => {
        setAddExport(true);
    };

    const handleDeleteExport = (e, key) => {
        e.stopPropagation();
        const x = { ...exports };
        x[key] = null;
        setExports(x);
    };

    const handleSave = (key, i, close) => {
        let d;

        switch (exports[key].export_type) {
            case 'syslog':
                d = syslogRef.current[i].getData();
                break;
            case 'elk':
                d = elkRef.current[i].getData();
                break;
            case 'raw':
                d = rawRef.current[i].getData();
                break;
            case 'bro':
                d = zeekRef.current[i].getData();
                break;
            case 'netflow':
                d = netflowRef.current[i].getData();
                break;
            case 'ipfix':
                d = ipfixRef.current[i].getData();
                break;
            case 'kafka':
                d = kafkaRef.current[i].getData();
                break;
            case 'webhook':
                d = webhookRef.current[i].getData();
                break;
            case 'snmp':
                d = snmpRef.current[i].getData();
                break;
        }

        setExports({
            ...exports,
            [key]: d,
        });

        if (close) {
            const ev = [...eventExportsOpen];
            ev[i] = false;
            setEventExportsOpen(ev);
        }

        setValid({ ...valid, [key]: true });
        toast.success('Export verification successful');
    };

    const handleTabChange = (key, t) => {
        const x = { ...exports };
        x[key].export_type = t;
        setExports(x);
    };

    const handleUpdateAddExportName = (e) => {
        setAddExportName(e.target.value);
    };

    const handleCreateExport = () => {
        const x = { ...exports };
        const exo = [...eventExportsOpen];

        if (!Object.keys(x).includes(addExportName)) {
            x[addExportName] = _cloneDeep(defaultConfigs[template]);
            setNewExports({ ...newExports, [addExportName]: template });
            setExports(x);
            exo.push(false);
            setEventExportsOpen(exo);
            setAddExport(false);
            setAddExportName('');
            setTemplate('Default');
        } else {
            toast.error('That name is already in use, please choose another...');
        }
    };

    const handleCancelCreateExport = () => {
        setAddExport(false);
        setAddExportName('');
        setTemplate('Default');
    };

    const handleUpdateEventExportOpen = (i) => {
        const ev = [...eventExportsOpen];
        ev[i] = !ev[i];
        setEventExportsOpen(ev);
    };

    const handlePush = () => {
        updateSystemSettings({ eexportd: { ...exports } });
    };

    const handleFilterReference = () => {
        window.open('/support/filter_reference');
    };

    const handleRevert = (k) => {
        setExports(_cloneDeep(original));
        navigate(-1);
    };

    const handleCancel = (k, i) => {
        const x = { ...exports };
        if (Object.prototype.hasOwnProperty.call(original, k)) {
            x[k] = original[k];
        } else {
            if (newExports[k]) {
                x[k] = _cloneDeep(defaultConfigs[newExports[k]]);
            } else {
                x[k] = _cloneDeep(defaultConfigs.Default);
            }
        }
        setExports(x);

        const ev = [...eventExportsOpen];
        ev[i] = false;
        setEventExportsOpen(ev);
        setValid({ ...valid, [k]: true });
    };

    const handleSetTemplate = (e) => {
        setTemplate(e.target.value);
    };

    return (
        <Grid container spacing={3} style={{ marginBottom: isFooter ? '20px' : '16px' }}>
            {cmcExportPerms.write && <Grid container item xs={12} justifyContent="flex-end">
                <IconButton
                    aria-label="open filter reference"
                    onClick={handleFilterReference}
                ><InfoIcon /></IconButton>
                <Box ml={2}><Button aria-label="add export" variant="contained" color="primary" onClick={handleAddExport}><AddIcon/> Export</Button></Box>
                <Box ml={2}><Button
                    aria-label="save"
                    variant="contained"
                    color="primary"
                    onClick={handlePush}
                    disabled={!changes || !(Object.keys(valid).reduce((a, c) => {
                        if (!a) {
                            return false;
                        } else {
                            return valid[c];
                        }
                    }, true))}
                >Save</Button></Box>
                <Box ml={2}><Button aria-label="revert" variant="contained" color="secondary" onClick={handleRevert}>Cancel</Button></Box>
            </Grid> }
            <Grid item xs={12} style={{ paddingTop: addExport ? '24px' : '0px' }}>
                <Box hidden={!addExport}>
                    <Paper>
                        <Box p={addExport ? 5 : 0}>
                            <Box display="flex">
                                <TextField
                                    value={addExportName}
                                    onChange={handleUpdateAddExportName}
                                    aria-label="export configuration name"
                                    placeholder="Export Configuration Name"
                                    fullWidth
                                />
                                <Box ml={2} mt={-2}>
                                    <InputLabel id="export-templates">Templates</InputLabel>
                                    <Select
                                        labelId="export-templates"
                                        style={{ width: '200px' }}
                                        value={template}
                                        onChange={handleSetTemplate}
                                    >
                                        {Object.keys(defaultConfigs).map((v, i) => {
                                            return (
                                                <MenuItem key={'default-config-' + i} value={v}>{v}</MenuItem>
                                            );
                                        })}
                                    </Select>
                                </Box>
                            </Box>
                            <Box mt={3} mb={5}>
                                <Button
                                    className={classes.buttonRight}
                                    variant="contained"
                                    color="primary"
                                    aria-label="create"
                                    onClick={handleCreateExport}
                                    disabled={!addExportName}
                                >Create</Button>
                                <Button className={classes.buttonRight} variant="contained" color="secondary" aria-label="cancel" onClick={handleCancelCreateExport}>Cancel</Button>
                            </Box>
                        </Box>
                    </Paper>
                </Box>
            </Grid>
            {Object.keys(exports).map((k, i) => {
                if (exports[k] == null) return null;
                return (
                    <Grid key={'eexportd-' + i} item xs={12}>
                        <Accordian expanded={eventExportsOpen[i]}>
                            <AccordianSummary aria-label="export summary" onClick={() => { handleUpdateEventExportOpen(i) }}>
                                <Grid container>
                                    <Grid item xs={12}>
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography variant="h5">
                                                Config: <code>{k}</code> <span className={classes.marginLeft}>Export: {exports[k].export_type === 'bro' ? 'zeek' : exports[k].export_type}</span>
                                            </Typography>
                                            {cmcExportPerms.write &&
                                                <IconButton
                                                    className={classes.buttonRight}
                                                    color="secondary"
                                                    aria-label="delete export"
                                                    onClick={(e) => { handleDeleteExport(e, k) }}
                                                    size="large"><DeleteIcon /></IconButton>
                                            }
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box value={exports[k].export_type} hidden={exports[k].export_type !== 'syslog'}>
                                            <span>Host: {exports[k].dest.host}</span>
                                            <span className={classes.marginLeft}>Port: {exports[k].dest.port}</span>
                                            <span className={classes.marginLeft}>Proto: {exports[k].dest.proto}</span>
                                            <span className={classes.marginLeft}>Facility: {handleFacilityLabel(exports[k].syslog.facility)}</span>
                                            <span className={classes.marginLeft}>Severity: {handleSeverityLabel(exports[k].syslog.severity)}</span>
                                            <span className={classes.marginLeft}>RFC: {exports[k].syslog.rfc}</span>
                                            <span className={classes.marginLeft}>Dest TZ: {exports[k].syslog.dst_tz}</span>
                                        </Box>
                                        <Box value={exports[k].export_type} hidden={exports[k].export_type !== 'elk'}>
                                            <span>Dest: {exports[k].dest.url}</span>
                                            <span className={classes.marginLeft}>Index:  {exports[k].elk.index}</span>
                                            <span className={classes.marginLeft}>Rotate: {exports[k].elk.index_rotate_days}</span>
                                            <span className={classes.marginLeft}>Dest TZ: {exports[k].elk.dst_tz}</span>
                                        </Box>
                                        <Box value={exports[k].export_type} hidden={exports[k].export_type !== 'kafka'}>
                                            <span>Brokers: {(exports[k].kafka && exports[k].kafka.brokers) || ''}</span>
                                            <span className={classes.marginLeft}>Topic:  {(exports[k].kafka && exports[k].kafka.topic) || ''}</span>
                                        </Box>
                                        <Box value={exports[k].export_type} hidden={exports[k].export_type !== 'webhook'}>
                                            <span>URL: {(exports[k].webhook && exports[k].webhook.url) || ''}</span>
                                        </Box>
                                        <Box value={exports[k].export_type} hidden={exports[k].export_type !== 'snmp'}>
                                            <span>Host: {exports[k].dest.host}</span>
                                        </Box>
                                        <Box value={exports[k].export_type} hidden={exports[k].export_type === 'syslog' || exports[k].export_type === 'elk' ||
                                            exports[k].export_type === 'kafka' || exports[k].export_type === 'webhook' || exports[k].export_type === 'snmp'}>
                                            <span>Host: {exports[k].dest.host}</span>
                                            <span className={classes.marginLeft}>Port: {exports[k].dest.port}</span>
                                            <span className={classes.marginLeft}>Proto: {exports[k].dest.proto}</span>
                                        </Box>
                                    </Grid>
                                </Grid>
                                <Box >
                                </Box>
                            </AccordianSummary>
                            <AccordianDetails>
                                <Grid container>
                                    <Grid item xs={12}>
                                        <Box display="flex">
                                            <Box className={classes.appbarItem} pr={5}>Export:</Box>
                                            <Tabs textColor='inherit' indicatorColor='secondary' className={classes.tabs} value={exports[k].export_type} onChange={(e, t) => { handleTabChange(k, t) }}>
                                                <Tab disabled={!cmcExportPerms.write} className={classes.tab} value="syslog" aria-label="Syslog" label="Syslog" />
                                                <Tab disabled={!cmcExportPerms.write} className={classes.tab} value="elk" aria-label="ES REST" label="ES REST" />
                                                <Tab disabled={!cmcExportPerms.write} className={classes.tab} value="raw" aria-label="Raw" label="Raw" />
                                                <Tab disabled={!cmcExportPerms.write} className={classes.tab} value="bro" aria-label="Zeek" label="Zeek" />
                                                <Tab disabled={!cmcExportPerms.write} className={classes.tab} value="netflow" aria-label="Netflow v9" label="Netflow v9" />
                                                <Tab disabled={!cmcExportPerms.write} className={classes.tab} value="ipfix" aria-label="IPFIX" label="IPFIX" />
                                                <Tab disabled={!cmcExportPerms.write} className={classes.tab} value="kafka" aria-label="Kafka" label="Kafka" />
                                                <Tab disabled={!cmcExportPerms.write} className={classes.tab} value="webhook" aria-label="Webhook" label="Webhook" />
                                                <Tab disabled={!cmcExportPerms.write} className={classes.tab} value="snmp" aria-label="SNMP" label="SNMP" />
                                            </Tabs>
                                        </Box>
                                        <Box p={2} className={classes.tabBG} value={exports[k].export_type} index={0} hidden={exports[k].export_type !== 'syslog'}>
                                            {exports[k].export_type === 'syslog' && <Syslog ref={ref => { syslogRef.current[i] = ref }} name={k} index={i} cmc data={exports[k]} onChange={handleChanges} onVerify={handleSave} onCancel={handleCancel} perms={cmcExportPerms} />}
                                        </Box>
                                        <Box p={2} className={classes.tabBG} value={exports[k].export_type} index={1} hidden={exports[k].export_type !== 'elk'}>
                                            {exports[k].export_type === 'elk' && <ESRest ref={ref => { elkRef.current[i] = ref }} name={k} index={i} cmc data={exports[k]} onChange={handleChanges} onVerify={handleSave} onCancel={handleCancel} perms={cmcExportPerms} />}
                                        </Box>
                                        <Box p={2} className={classes.tabBG} value={exports[k].export_type} index={2} hidden={exports[k].export_type !== 'raw'}>
                                            {exports[k].export_type === 'raw' && <Raw ref={ref => { rawRef.current[i] = ref }} name={k} index={i} cmc data={exports[k]} onChange={handleChanges} onVerify={handleSave} onCancel={handleCancel} perms={cmcExportPerms} />}
                                        </Box>
                                        <Box p={2} className={classes.tabBG} value={exports[k].export_type} index={3} hidden={exports[k].export_type !== 'bro'}>
                                            {exports[k].export_type === 'bro' && <Zeek ref={ref => { zeekRef.current[i] = ref }} name={k} index={i} cmc data={exports[k]} onChange={handleChanges} onVerify={handleSave} onCancel={handleCancel} perms={cmcExportPerms} />}
                                        </Box>
                                        <Box p={2} className={classes.tabBG} value={exports[k].export_type} index={4} hidden={exports[k].export_type !== 'netflow'}>
                                            {exports[k].export_type === 'netflow' && <Netflow ref={ref => { netflowRef.current[i] = ref }} name={k} index={i} cmc data={exports[k]} onChange={handleChanges} onVerify={handleSave} onCancel={handleCancel} perms={cmcExportPerms} />}
                                        </Box>
                                        <Box p={2} className={classes.tabBG} value={exports[k].export_type} index={5} hidden={exports[k].export_type !== 'ipfix'}>
                                            {exports[k].export_type === 'ipfix' && <IPFix ref={ref => { ipfixRef.current[i] = ref }} name={k} index={i} cmc data={exports[k]} onChange={handleChanges} onVerify={handleSave} onCancel={handleCancel} perms={cmcExportPerms} />}
                                        </Box>
                                        <Box p={2} className={classes.tabBG} value={exports[k].export_type} index={6} hidden={exports[k].export_type !== 'kafka'}>
                                            {exports[k].export_type === 'kafka' && <Kafka ref={ref => { kafkaRef.current[i] = ref }} name={k} index={i} cmc data={exports[k]} onChange={handleChanges} onVerify={handleSave} onCancel={handleCancel} perms={cmcExportPerms} />}
                                        </Box>
                                        <Box p={2} className={classes.tabBG} value={exports[k].export_type} index={7} hidden={exports[k].export_type !== 'webhook'}>
                                            {exports[k].export_type === 'webhook' && <Webhook ref={ref => { webhookRef.current[i] = ref }} name={k} index={i} cmc data={exports[k]} onChange={handleChanges} onVerify={handleSave} onCancel={handleCancel} perms={cmcExportPerms} />}
                                        </Box>
                                        <Box p={2} className={classes.tabBG} value={exports[k].export_type} index={7} hidden={exports[k].export_type !== 'snmp'}>
                                            {exports[k].export_type === 'snmp' && <Snmp ref={ref => { snmpRef.current[i] = ref }} name={k} index={i} cmc data={exports[k]} onChange={handleChanges} onVerify={handleSave} onCancel={handleCancel} perms={cmcExportPerms} />}
                                        </Box>
                                    </Grid>
                                </Grid>
                            </AccordianDetails>
                        </Accordian>
                    </Grid>
                );
            })}
        </Grid>
    );
};

export default Event;
